"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SyncMode = exports.Mesh = void 0;
const model_1 = require("../../data/model");
const multimap_1 = require("../../util/multimap");
const network_1 = require("../agents/network");
const peer_1 = require("../agents/peer");
const state_1 = require("../agents/state");
const discovery_1 = require("../agents/discovery");
const AgentPod_1 = require("./AgentPod");
const linkup_1 = require("../../net/linkup");
const random_1 = require("../../crypto/random");
var SyncMode;
(function (SyncMode) {
    SyncMode["single"] = "single";
    SyncMode["full"] = "full";
    SyncMode["recursive"] = "recursive"; // sync the object, and any mutable object referenced by it or its mutation ops.
})(SyncMode || (SyncMode = {}));
exports.SyncMode = SyncMode;
class Mesh {
    // configuration
    constructor(networkProxy) {
        this.pod = new AgentPod_1.AgentPod();
        this.network = new network_1.NetworkAgent(new linkup_1.LinkupManager(), networkProxy);
        this.pod.registerAgent(this.network);
        this.secured = new network_1.SecureNetworkAgent();
        this.pod.registerAgent(this.secured);
        this.usage = new multimap_1.MultiMap();
        this.usageTokens = new Map();
        this.gossipIdsPerPeerGroup = new multimap_1.MultiMap();
        this.syncAgents = new Map();
        this.rootObjects = new Map();
        this.rootObjectStores = new Map();
        this.gossipIdsPerObject = new multimap_1.MultiMap();
        this.allNewOpCallbacks = new Map();
        this.allRootAncestors = new Map();
        this.allDependencyClosures = new Map();
    }
    // PeerGroups: join, leave
    joinPeerGroup(pg, config, usageToken) {
        let token = this.registerUsage({ type: 'peer-group', peerGroupId: pg.id }, usageToken);
        let agent = this.pod.getAgent(peer_1.PeerGroupAgent.agentIdForPeerGroup(pg.id));
        if (agent === undefined) {
            agent = new peer_1.PeerGroupAgent(pg.id, pg.localPeer, pg.peerSource, config);
            this.pod.registerAgent(agent);
        }
        return token;
    }
    leavePeerGroup(token) {
        const usageInfo = this.deregisterUsage(token, 'peer-group');
        if (usageInfo !== undefined) {
            const usageKey = Mesh.createUsageKey(usageInfo);
            if (this.usage.get(usageKey).size === 0) {
                const agentId = peer_1.PeerGroupAgent.agentIdForPeerGroup(usageInfo.peerGroupId);
                let agent = this.pod.getAgent(agentId);
                if (agent !== undefined) {
                    this.pod.deregisterAgent(agent);
                }
            }
        }
    }
    // Object synchronization
    syncObjectWithPeerGroup(peerGroupId, obj, mode = SyncMode.full, gossipId, usageToken) {
        let peerGroup = this.pod.getAgent(peer_1.PeerGroupAgent.agentIdForPeerGroup(peerGroupId));
        if (peerGroup === undefined) {
            throw new Error("Cannot sync object with mesh " + peerGroupId + ", need to join it first.");
        }
        if (gossipId === undefined) {
            gossipId = peerGroupId;
        }
        let gossip = this.pod.getAgent(state_1.StateGossipAgent.agentIdForGossip(gossipId));
        if (gossip === undefined) {
            gossip = new state_1.StateGossipAgent(gossipId, peerGroup);
            this.pod.registerAgent(gossip);
        }
        else if (gossip.getPeerControl().peerGroupId !== peerGroupId) {
            throw new Error('The gossip id ' + gossipId + ' is already in use buy peer group ' + gossip.getPeerControl().peerGroupId);
        }
        this.addRootSync(gossip, obj, mode);
        this.gossipIdsPerPeerGroup.add(peerGroupId, gossipId);
        return this.registerUsage({ type: 'object-sync', objHash: obj.getLastHash(), peerGroupId: peerGroupId, gossipId: gossipId }, usageToken);
    }
    syncManyObjectsWithPeerGroup(peerGroupId, objs, mode = SyncMode.full, gossipId, usageTokens) {
        const tokens = new Map();
        for (const obj of objs) {
            const usageToken = this.syncObjectWithPeerGroup(peerGroupId, obj, mode, gossipId, usageTokens === null || usageTokens === void 0 ? void 0 : usageTokens.get(obj.getLastHash()));
            tokens.set(obj.getLastHash(), usageToken);
        }
        return tokens;
    }
    stopSyncObjectWithPeerGroup(usageToken) {
        const usageInfo = this.deregisterUsage(usageToken, 'object-sync');
        if (usageInfo !== undefined) {
            const usageKey = Mesh.createUsageKey(usageInfo);
            if (this.usage.get(usageKey).size === 0) {
                const peerGroupId = usageInfo.peerGroupId;
                const hash = usageInfo.objHash;
                const gossipId = usageInfo.gossipId;
                let gossip = this.pod.getAgent(state_1.StateGossipAgent.agentIdForGossip(gossipId));
                if (gossip !== undefined) {
                    this.removeRootSync(gossip, hash);
                    let roots = this.rootObjects.get(gossipId);
                    if (roots === undefined || roots.size === 0) {
                        this.pod.deregisterAgent(gossip);
                        this.gossipIdsPerPeerGroup.delete(peerGroupId, gossipId);
                    }
                }
            }
        }
    }
    stopSyncManyObjectsWithPeerGroup(tokens) {
        for (const token of tokens) {
            this.stopSyncObjectWithPeerGroup(token);
        }
    }
    // Object discovery
    startObjectBroadcast(object, linkupServers, replyEndpoints, broadcastedSuffixBits, usageToken) {
        if (broadcastedSuffixBits === undefined) {
            broadcastedSuffixBits = discovery_1.ObjectBroadcastAgent.defaultBroadcastedSuffixBits;
        }
        const agentId = discovery_1.ObjectBroadcastAgent.agentIdForHash(object.hash(), broadcastedSuffixBits);
        let broadcastAgent = this.pod.getAgent(agentId);
        if (broadcastAgent === undefined) {
            broadcastAgent = new discovery_1.ObjectBroadcastAgent(object, broadcastedSuffixBits);
            this.pod.registerAgent(broadcastAgent);
        }
        broadcastAgent.listenOn(linkupServers, replyEndpoints);
        return this.registerUsage({ type: 'object-broadcast', objHash: object.getLastHash(), broadcastedSuffixBits: broadcastedSuffixBits }, usageToken);
    }
    stopObjectBroadcast(token) {
        const usageInfo = this.deregisterUsage(token, 'object-broadcast');
        if (usageInfo !== undefined) {
            const usageKey = Mesh.createUsageKey(usageInfo);
            if (this.usage.get(usageKey).size === 0) {
                const hash = usageInfo.objHash;
                const broadcastedSuffixBits = usageInfo.broadcastedSuffixBits;
                const agentId = discovery_1.ObjectBroadcastAgent.agentIdForHash(hash, broadcastedSuffixBits);
                let broadcastAgent = this.pod.getAgent(agentId);
                broadcastAgent === null || broadcastAgent === void 0 ? void 0 : broadcastAgent.shutdown();
            }
        }
    }
    findObjectByHash(hash, linkupServers, replyEndpoint, count = 1, maxAge = 30, strictEndpoints = false) {
        const suffix = model_1.Hashing.toHex(hash);
        return this.findObjectByHashSuffix(suffix, linkupServers, replyEndpoint, count, maxAge, strictEndpoints);
    }
    findObjectByHashSuffix(hashSuffix, linkupServers, replyEndpoint, count = 1, maxAge = 30, strictEndpoints = false) {
        const discoveryAgent = this.getDiscoveryAgentFor(hashSuffix);
        discoveryAgent.query(linkupServers, replyEndpoint, count);
        let params = {};
        params.maxAge = maxAge;
        if (strictEndpoints) {
            params.linkupServers = linkupServers;
            params.localEndpoints = [replyEndpoint];
        }
        return discoveryAgent.getReplyStream(params);
    }
    findObjectByHashRetry(hash, linkupServers, replyEndpoint, count = 1) {
        const suffix = model_1.Hashing.toHex(hash);
        this.findObjectByHashSuffixRetry(suffix, linkupServers, replyEndpoint, count);
    }
    findObjectByHashSuffixRetry(hashSuffix, linkupServers, replyEndpoint, count = 1) {
        const discoveryAgent = this.getDiscoveryAgentFor(hashSuffix);
        discoveryAgent.query(linkupServers, replyEndpoint, count);
    }
    getDiscoveryAgentFor(hashSuffix) {
        const agentId = discovery_1.ObjectDiscoveryAgent.agentIdForHexHashSuffix(hashSuffix);
        let discoveryAgent = this.pod.getAgent(agentId);
        if (discoveryAgent !== undefined && discoveryAgent.wasShutdown) {
            this.pod.deregisterAgent(discoveryAgent);
            discoveryAgent = undefined;
        }
        if (discoveryAgent === undefined) {
            discoveryAgent = new discovery_1.ObjectDiscoveryAgent(hashSuffix);
            this.pod.registerAgent(discoveryAgent);
        }
        return discoveryAgent;
    }
    addRootSync(gossip, obj, mode) {
        var _a;
        const gossipId = gossip.gossipId;
        let roots = this.rootObjects.get(gossipId);
        if (roots === undefined) {
            roots = new Map();
            this.rootObjects.set(gossipId, roots);
        }
        let rootStores = this.rootObjectStores.get(gossipId);
        if (rootStores === undefined) {
            rootStores = new Map();
            this.rootObjectStores.set(gossipId, rootStores);
        }
        let hash = obj.hash();
        let oldMode = roots.get(hash);
        if (oldMode === undefined) {
            roots.set(hash, mode);
            rootStores.set(hash, (_a = obj.getResources()) === null || _a === void 0 ? void 0 : _a.store);
            if (mode === SyncMode.single) {
                if (obj instanceof model_1.MutableObject) {
                    this.addSingleObjectSync(gossip, hash, obj);
                }
                else {
                    throw new Error('Asked to sync object in single mode, but it is not mutable, so there is nothing to do.');
                }
            }
            else {
                this.addFullObjectSync(gossip, obj, hash, mode);
            }
        }
        else if (oldMode !== mode) {
            throw new Error('The object ' + hash + ' was already being gossiped on ' + gossipId + ', but with a different mode. Gossiping with more than one mode is not supported.');
        }
    }
    removeRootSync(gossip, objHash) {
        let roots = this.rootObjects.get(gossip.gossipId);
        let rootStores = this.rootObjectStores.get(gossip.gossipId);
        if (roots !== undefined) {
            let oldMode = roots.get(objHash);
            if (oldMode !== undefined) {
                roots.delete(objHash);
                if (oldMode === SyncMode.single) {
                    let modes = this.getAllModesForObject(gossip, objHash);
                    if (modes.size === 0) {
                        this.removeSingleObjectSync(gossip, objHash);
                    }
                }
                else {
                    const store = rootStores === null || rootStores === void 0 ? void 0 : rootStores.get(objHash);
                    this.removeFullObjectSync(gossip, objHash, objHash, oldMode, store);
                }
            }
        }
    }
    // get all the modes this objHash is being synced within all gossip ids
    // that share their peer group with the provided one.
    getAllModesForObject(gossip, objHash) {
        var _a;
        let modes = new Set();
        if (gossip !== undefined) {
            let peerGroupId = gossip.peerGroupAgent.peerGroupId;
            let matchGossipIds = this.gossipIdsPerPeerGroup.get(peerGroupId);
            if (matchGossipIds !== undefined) {
                for (const matchGossipId of matchGossipIds) {
                    let roots = this.rootObjects.get(matchGossipId);
                    let mode = roots === null || roots === void 0 ? void 0 : roots.get(objHash);
                    if (mode !== undefined) {
                        modes.add(mode);
                    }
                    let rootAncestors = (_a = this.allRootAncestors.get(matchGossipId)) === null || _a === void 0 ? void 0 : _a.get(objHash);
                    if (rootAncestors !== undefined) {
                        for (const rootHash of rootAncestors) {
                            let rootMode = roots === null || roots === void 0 ? void 0 : roots.get(rootHash);
                            if (rootMode !== undefined) {
                                modes.add(rootMode);
                            }
                        }
                    }
                }
            }
            return modes;
        }
        return modes;
    }
    addFullObjectSync(gossip, obj, root, mode) {
        const gossipId = gossip.gossipId;
        let hash = obj.hash();
        let dependencies = this.allDependencyClosures.get(gossipId);
        if (dependencies === undefined) {
            dependencies = new multimap_1.MultiMap();
            this.allDependencyClosures.set(gossipId, dependencies);
        }
        if (!dependencies.get(root).has(hash)) {
            let rootAncestors = this.allRootAncestors.get(gossipId);
            if (rootAncestors === undefined) {
                rootAncestors = new multimap_1.MultiMap();
                this.allRootAncestors.set(gossipId, rootAncestors);
            }
            let targets = new Map();
            if (mode === SyncMode.single) {
                if (obj instanceof model_1.MutableObject) {
                    targets.set(hash, obj);
                }
            }
            else {
                // (mode === SyncMode.subobjects || mode === SyncMode.mutations)
                let context = obj.toContext();
                for (let [hash, dep] of context.objects.entries()) {
                    if (dep instanceof model_1.MutableObject) {
                        targets.set(hash, dep);
                    }
                }
            }
            for (const [thash, target] of targets.entries()) {
                this.addSingleObjectSync(gossip, thash, target);
                dependencies.add(root, thash);
                rootAncestors.add(thash, root);
                if (mode === SyncMode.recursive) {
                    this.watchForNewOps(gossip, target);
                    this.trackOps(gossip, target, root);
                }
            }
        }
    }
    removeFullObjectSync(gossip, mutHash, oldRootHash, oldMode, store) {
        var _a;
        let depClosures = this.allDependencyClosures.get(gossip.gossipId);
        let depClosure = depClosures === null || depClosures === void 0 ? void 0 : depClosures.get(mutHash);
        if (depClosure !== undefined) {
            depClosures === null || depClosures === void 0 ? void 0 : depClosures.deleteKey(mutHash);
            for (const depHash of depClosure) {
                (_a = this.allRootAncestors.get(gossip.gossipId)) === null || _a === void 0 ? void 0 : _a.delete(depHash, oldRootHash);
            }
            for (const depHash of depClosure) {
                const modes = this.getAllModesForObject(gossip, depHash);
                if (modes.size === 0) {
                    this.removeSingleObjectSync(gossip, depHash);
                }
                if (oldMode === SyncMode.recursive && !modes.has(SyncMode.recursive)) {
                    this.unwatchForNewOps(gossip, depHash, store);
                }
            }
        }
    }
    addSingleObjectSync(gossip, mutHash, mut) {
        const peerGroup = gossip.peerGroupAgent;
        const peerGroupId = peerGroup.peerGroupId;
        let peerGroupSyncAgents = this.syncAgents.get(peerGroupId);
        if (peerGroupSyncAgents === undefined) {
            peerGroupSyncAgents = new Map();
            this.syncAgents.set(peerGroupId, peerGroupSyncAgents);
        }
        let sync = peerGroupSyncAgents.get(mutHash);
        if (sync === undefined) {
            sync = mut.createSyncAgent(gossip.peerGroupAgent);
            peerGroupSyncAgents.set(mutHash, sync);
            gossip.trackAgentState(sync.getAgentId());
            this.pod.registerAgent(sync);
        }
    }
    removeSingleObjectSync(gossip, mutHash) {
        if (gossip !== undefined) {
            const peerGroup = gossip.peerGroupAgent;
            const peerGroupId = peerGroup.peerGroupId;
            let peerGroupSyncAgents = this.syncAgents.get(peerGroupId);
            let sync = peerGroupSyncAgents === null || peerGroupSyncAgents === void 0 ? void 0 : peerGroupSyncAgents.get(mutHash);
            if (sync !== undefined) {
                gossip.untrackAgentState(sync.getAgentId());
                this.pod.deregisterAgent(sync);
                peerGroupSyncAgents === null || peerGroupSyncAgents === void 0 ? void 0 : peerGroupSyncAgents.delete(mutHash);
            }
        }
    }
    // recursive tracking of subobjects for state gossip & sync
    // Fetch existing ops on the databse and check if there are any mutable
    // references to track.
    async trackOps(gossip, mut, root) {
        let validOpClasses = mut.getAcceptedMutationOpClasses();
        let refs = await mut.getStore().loadByReference('target', mut.hash());
        for (let obj of refs.objects) {
            if (validOpClasses.indexOf(obj.getClassName()) >= 0) {
                this.addFullObjectSync(gossip, mut, root, SyncMode.recursive);
            }
        }
    }
    watchForNewOps(gossip, mut) {
        let newOpCallbacks = this.allNewOpCallbacks.get(gossip.gossipId);
        if (newOpCallbacks === undefined) {
            newOpCallbacks = new Map();
            this.allNewOpCallbacks.set(gossip.gossipId, newOpCallbacks);
        }
        let hash = mut.hash();
        if (!newOpCallbacks.has(hash)) {
            let callback = async (opHash) => {
                var _a, _b;
                let op = await mut.getStore().load(opHash);
                if (op !== undefined &&
                    mut.getAcceptedMutationOpClasses().indexOf(op.getClassName()) >= 0) {
                    let mutOp = op;
                    const roots = (_a = this.allRootAncestors.get(gossip.gossipId)) === null || _a === void 0 ? void 0 : _a.get(mutOp.getTarget().hash());
                    if (roots !== undefined) {
                        for (const rootHash of roots) {
                            if (((_b = this.rootObjects.get(gossip.gossipId)) === null || _b === void 0 ? void 0 : _b.get(rootHash)) === SyncMode.recursive) {
                                this.addFullObjectSync(gossip, op, rootHash, SyncMode.recursive);
                            }
                        }
                    }
                }
            };
            newOpCallbacks.set(hash, callback);
            mut.getStore().watchReferences('target', mut.hash(), callback);
        }
    }
    unwatchForNewOps(gossip, mutHash, store) {
        let newOpCallbacks = this.allNewOpCallbacks.get(gossip.gossipId);
        const callback = newOpCallbacks === null || newOpCallbacks === void 0 ? void 0 : newOpCallbacks.get(mutHash);
        if (callback !== undefined) {
            store.removeReferencesWatch('target', mutHash, callback);
            newOpCallbacks === null || newOpCallbacks === void 0 ? void 0 : newOpCallbacks.delete(mutHash);
        }
    }
    registerUsage(usageInfo, usageToken) {
        const token = usageToken || Mesh.createUsageToken();
        this.usageTokens.set(token, usageInfo);
        const usageKey = Mesh.createUsageKey(usageInfo);
        this.usage.add(usageKey, token);
        return token;
    }
    deregisterUsage(token, expectedType) {
        const usageInfo = this.usageTokens.get(token);
        if (usageInfo !== undefined) {
            if (usageInfo.type !== expectedType) {
                throw new Error('Refusing to deregister usage token ' + token + ': it is being used as for ' + expectedType + ', but originally was for ' + usageInfo.type);
            }
            const usageKey = Mesh.createUsageKey(usageInfo);
            this.usage.delete(usageKey, token);
            this.usageTokens.delete(token);
            return usageInfo;
        }
        else {
            return undefined;
        }
    }
    static createUsageToken() {
        return new random_1.RNGImpl().randomHexString(128);
    }
    static createUsageKey(usageInfo) {
        if (usageInfo.type === 'peer-group') {
            return usageInfo.type + '-' + usageInfo.peerGroupId.replace(/[-]/g, '--');
        }
        else if (usageInfo.type === 'object-sync') {
            return usageInfo.type + '-' + usageInfo.peerGroupId.replace(/[-]/g, '--') + '-' + usageInfo.gossipId.replace(/[-]/g, '--');
        }
        else {
            return usageInfo.type + '-' + usageInfo.objHash + '-' + usageInfo.broadcastedSuffixBits;
        }
    }
}
exports.Mesh = Mesh;
//# sourceMappingURL=Mesh.js.map